<!--
  DETTAGLIO SPOSTAMENTO INTERNO.

  Stessa impaginazione del dettaglio prenotazione (`DettaglioPrenotazionePage`):
  le CORSE a sinistra, dove si legge cosa succede, il riepilogo a destra, con
  pastiglie di stato e chip del servizio condivisi (StatusPill, ServiceChip).

  «Percorso» e «Missione» sono nomi interni: a schermo il servizio si chiama
  SPOSTAMENTO INTERNO. Vedi le trappole di naming.

  ⚠️ `two_operators === false` è lo spostamento a UN operatore.
-->
<template>
  <div
    v-loading="loading"
    class="dj"
  >
    <!-- ---------- intestazione ---------- -->
    <div class="dj__head">
      <button
        type="button"
        class="dj__back"
        aria-label="Torna indietro"
        @click="goBack"
      >
        ‹
      </button>
      <div class="dj__headText">
        <div class="dj__titleRow">
          <h1 class="dj__title">
            Spostamento del {{ giornoEsteso }}
          </h1>
          <StatusPill
            v-if="status"
            :kind="statoPill.kind"
            :label="statoPill.label"
          />
        </div>
        <div class="dj__sub">
          <ServiceChip service="journey" />
          <span>{{ corseLabel }}</span>
        </div>
      </div>

      <div class="dj__actions">
        <!-- In modifica: Annulla + Salva -->
        <template v-if="isEditing">
          <el-button @click="cancelEdit">
            Annulla
          </el-button>
          <el-button
            type="primary"
            @click="saveEdit"
          >
            Salva modifiche
          </el-button>
        </template>
        <!-- Modifiche ED eliminazioni si chiudono alle 15:00 del giorno
             prima: dopo, la pagina è in sola lettura e per cambiare qualcosa
             si passa da Simba. -->
        <template v-else-if="canEdit">
          <el-button
            type="primary"
            plain
            @click="enterEdit"
          >
            Modifica
          </el-button>
        </template>
      </div>
    </div>

    <div
      v-if="!canEdit && journey"
      class="dj__now"
    >
      <span class="dj__dot" />
      Modifiche ed eliminazioni chiuse alle 15:00 del giorno prima: per cambiare qualcosa contatta Simba.
    </div>

    <!-- ================= MODIFICA =================
         Stesso editor della creazione. :key forza il re-seed a ogni sessione.
         A tutta larghezza: l'editor ha la sua timeline e non sta in colonna. -->
    <section
      v-if="isEditing"
      class="dj__card"
    >
      <header class="dj__cardHead">
        <h3>Modifica corse</h3>
      </header>
      <div class="dj__cardBody">
        <journey-legs-editor
          :key="editSession"
          ref="legsEditor"
          :initial-routes="editRoutes"
          :date="editorDate"
          :journey-id="id"
          :giornata="giornata"
          :locked="!canEdit"
          @route-removed="onRouteRemoved"
        />

        <!-- Gli stessi due pulsanti dell'intestazione, ripetuti qui: con più
             corse l'editor è lungo e per salvare bisognava risalire in cima. -->
        <div class="dj__actions dj__actions--foot">
          <el-button @click="cancelEdit">
            Annulla
          </el-button>
          <el-button
            type="primary"
            @click="saveEdit"
          >
            Salva modifiche
          </el-button>
        </div>
      </div>
    </section>

    <div
      v-else
      class="dj__grid"
    >
      <!-- ================= CORSE ================= -->
      <div class="dj__col">
        <section
          v-for="(leg, i) in routes"
          :key="leg.id || i"
          class="dj__card"
        >
          <header class="dj__cardHead">
            <h3>Corsa {{ i + 1 }}</h3>
            <!-- Il numero di operatori non si colora: è un attributo della
                 corsa, non un servizio né uno stato. -->
            <span class="dj__tag">{{ operatoriLabel(leg) }}</span>
            <StatusPill
              v-if="datiMancanti(leg).length"
              kind="attn"
              label="Da completare"
            />
            <StatusPill
              v-else-if="leg.status"
              :kind="statoCorsa(leg).kind"
              :label="statoCorsa(leg).label"
            />
            <span class="dj__spacer" />
            <button
              v-if="leg.id"
              type="button"
              class="dj__tool"
              title="Foto della corsa (partenza e arrivo)"
              @click="openRoutePhotos(leg)"
            >
              <el-icon><Camera /></el-icon>
              Foto
            </button>
            <!-- Si elimina la singola corsa, non la giornata intera. Qui e
                 non solo nell'editor: lì il cestino non toglie mai l'ultima
                 corsa rimasta. Fino alle 15:00 del giorno prima. -->
            <button
              v-if="leg.id && canEdit"
              type="button"
              class="dj__tool dj__tool--danger"
              :disabled="eliminandoCorsa === leg.id"
              @click="eliminaCorsa(leg, i)"
            >
              {{ eliminandoCorsa === leg.id ? 'Elimino…' : 'Elimina corsa' }}
            </button>
          </header>

          <div class="dj__cardBody">
            <!-- Corsa incompleta: mancano dati del veicolo o del
                 percorso, cioè quelli che inserisce il partner. Il driver non
                 conta: lo assegna Simba. -->
            <p
              v-if="datiMancanti(leg).length"
              class="dj__missing"
            >
              Mancano: {{ datiMancanti(leg).join(', ') }}
            </p>

            <!-- Veicolo PER PRIMO, allineato a sinistra: in uno spostamento
                 interno è l'auto che identifica la corsa, come nel dettaglio
                 prenotazione. -->
            <div class="dj__car">
              <span
                class="dj__carPlate"
                :class="{ 'dj__carPlate--empty': !leg.targa && leg.immatricolato !== false }"
              >{{ leg.targa || (leg.immatricolato === false ? 'Targa prova' : 'Targa mancante') }}</span>
              <div class="dj__carInfo">
                <div
                  class="dj__carName"
                  :class="{ 'dj__carName--empty': !leg.marca && !leg.modello }"
                >
                  {{ [leg.marca, leg.modello].filter(Boolean).join(' ') || 'Marca e modello mancanti' }}
                </div>
                <!-- Veicolo senza immatricolazione: si muove con la targa prova
                     e la corsa ha una tariffa sua. Sta qui, con marca e cambio,
                     perché è un dato del veicolo. -->
                <!-- Senza targa il riquadro della targa dice già «Targa
                     prova»: la pastiglia servirebbe solo a ripeterlo. -->
                <div
                  v-if="leg.gearbox_type || (leg.immatricolato === false && leg.targa)"
                  class="dj__carMeta"
                >
                  <span
                    v-if="leg.immatricolato === false && leg.targa"
                    class="dj__tag dj__tag--imm"
                    title="Veicolo non immatricolato: si sposta con la targa prova"
                  >Targa prova</span>
                  <span
                    v-if="leg.gearbox_type"
                    class="dj__meta"
                  >Cambio {{ leg.gearbox_type === 'AUTOMATIC' ? 'automatico' : 'manuale' }}</span>
                </div>
              </div>
              <!-- Le note della corsa ci sono sempre: vuote si dice, così si
                   sa che non c'è niente da leggere e non che manchino. -->
              <p class="dj__carNote">
                <span class="dj__carNoteK">Note</span>
                <span
                  v-if="leg.details"
                  class="dj__carNoteT"
                >{{ leg.details }}</span>
                <span
                  v-else
                  class="dj__carNoteVuota"
                >Nessuna nota</span>
              </p>
            </div>

            <div class="dj__legTop">
              <!-- Partenza e arrivo, ognuno con il suo orario e il luogo: il
                   nome della sede, quando è una sede, e sotto l'indirizzo. -->
              <div class="dj__stops">
                <div
                  v-for="tappa in tappe(leg)"
                  :key="tappa.chiave"
                  class="dj__stop"
                >
                  <span class="dj__stopK">{{ tappa.etichetta }}</span>
                  <span class="dj__stopH">{{ tappa.ora }}</span>
                  <div class="dj__stopP">
                    <div
                      v-if="tappa.sede"
                      class="dj__stopN"
                    >
                      {{ tappa.sede }}
                    </div>
                    <div :class="tappa.sede ? 'dj__addr dj__addr--sub' : 'dj__addr'">
                      {{ tappa.indirizzo }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Km e minuti in colonna, a destra: si leggono insieme. -->
              <div
                v-if="leg.km != null || leg.min != null"
                class="dj__km"
              >
                <template v-if="leg.km != null">
                  <div class="dj__kmN">
                    {{ numero(leg.km) }}
                  </div>
                  <div class="dj__kmL">
                    km
                  </div>
                </template>
                <template v-if="leg.min != null">
                  <div class="dj__kmN dj__kmN--min">
                    {{ Math.round(Number(leg.min)) }}
                  </div>
                  <div class="dj__kmL">
                    min
                  </div>
                </template>
              </div>
            </div>

            <!-- Costo con le tariffe del partner. Un costo che non si può
                 calcolare dice perché, invece di «€ 0,00». -->
            <div
              v-if="costi[leg.id]"
              class="dj__kvRow"
            >
              <div
                class="dj__kvCell"
              >
                <span class="dj__k">Costo</span>
                <span
                  class="dj__v"
                  :class="costi[leg.id].ok ? 'dj__num' : 'dj__v--attn'"
                >{{ costi[leg.id].testo }}</span>
              </div>
              <!-- Benzina fatta dal driver a quest'auto: rimborso a parte. -->
              <div
                v-if="Number(leg.carburante)"
                class="dj__kvCell"
              >
                <span class="dj__k">Benzina</span>
                <span class="dj__v dj__num">{{ importo(Number(leg.carburante)) }}</span>
              </div>
            </div>

            <div class="dj__sep" />

            <!-- Chi arriva. In sola lettura: l'assegnazione la fa Simba. -->
            <div class="dj__people">
              <div
                v-for="p in persone(leg)"
                :key="p.chiave || p.ruolo"
                class="dj__person"
              >
                <span
                  class="dj__ava"
                  :class="{ 'dj__ava--empty': !p.nome }"
                >{{ p.nome ? iniziali(p.nome) : '—' }}</span>
                <div>
                  <div
                    class="dj__personName"
                    :class="{ 'dj__personName--empty': !p.nome }"
                  >
                    {{ p.nome || 'Non assegnato' }}
                    <!-- Come rientra il driver: a richiesta, con la «i». -->
                    <el-tooltip
                      v-if="p.ruolo === 'Driver' && rientri.get(leg.id)"
                      :content="infoRientro(leg)"
                      placement="top"
                      effect="light"
                    >
                      <span
                        class="dj__info"
                        :class="{ 'dj__info--attn': rientri.get(leg.id).tipo === 'scoperto' }"
                        tabindex="0"
                        role="img"
                        :aria-label="infoRientro(leg)"
                      >i</span>
                    </el-tooltip>
                  </div>
                  <div class="dj__meta">
                    {{ p.ruolo }}<template v-if="p.telefono">
                      · <a
                        class="dj__tel"
                        :href="`tel:${p.telefono}`"
                      >{{ p.telefono }}</a>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ---- allegati della corsa ----
               DDT, moduli firmati, ricevute. I file stanno sull'object storage
               e qui arrivano link temporanei; li vedono anche Simba e il
               driver. -->
          <div
            v-if="leg.id"
            class="dj__atts"
          >
            <div class="dj__attsHead">
              <span class="dj__attsTitle">Allegati</span>
              <span
                v-if="allegati[leg.id]?.stato === 'ok'"
                class="dj__count"
              >{{ allegati[leg.id].lista.length }}</span>
              <span class="dj__spacer" />
              <label
                class="dj__upload"
                :class="{ 'is-busy': inCaricamento[leg.id] }"
              >
                <input
                  type="file"
                  class="dj__file"
                  :disabled="inCaricamento[leg.id]"
                  :aria-label="`Aggiungi un allegato alla corsa ${i + 1}`"
                  @change="(e) => caricaAllegato(leg, e)"
                >
                {{ inCaricamento[leg.id] ? 'Carico…' : '+ Aggiungi' }}
              </label>
            </div>

            <p
              v-if="!allegati[leg.id] || allegati[leg.id].stato === 'loading'"
              class="dj__meta"
            >
              Caricamento…
            </p>
            <p
              v-else-if="allegati[leg.id].stato === 'errore'"
              class="dj__meta"
            >
              Non riesco a leggere gli allegati.
            </p>
            <p
              v-else-if="!allegati[leg.id].lista.length"
              class="dj__meta"
            >
              Nessun allegato.
            </p>
            <template v-else>
              <div
                v-for="a in allegati[leg.id].lista"
                :key="a.id"
                class="dj__att"
              >
                <div class="dj__attText">
                  <button
                    type="button"
                    class="dj__attName"
                    @click="apriAllegato(leg, a)"
                  >
                    {{ a.file_name }}
                  </button>
                  <span class="dj__meta">
                    {{ dimensione(a.size_bytes) }} · {{ formatPhotoDate(a.created_at) }}<template v-if="a.uploaded_by === 'driver'"> · dal driver</template>
                  </span>
                </div>
                <!-- Quelli del driver non si eliminano da qui: sono i documenti
                   della corsa (DDT, firme), non un file caricato per sbaglio. -->
                <button
                  v-if="a.uploaded_by !== 'driver'"
                  type="button"
                  class="dj__attDel"
                  :aria-label="`Elimina ${a.file_name}`"
                  @click="eliminaAllegato(leg, a)"
                >
                  ×
                </button>
              </div>
            </template>
          </div>
        </section>

        <div
          v-if="!loading && routes.length === 0"
          class="dj__empty"
        >
          Nessuna corsa in questo spostamento.
        </div>
        <!-- In fondo, la giornata: le sedi e gli spostamenti delle auto. -->
        <MappaSpostamenti
          :mappa="mappaSalvata"
          class="dj__giri"
        />
      </div>

      <!-- ================= RIEPILOGO ================= -->
      <aside class="dj__aside">
        <section class="dj__card">
          <header class="dj__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="dj__cardBody">
            <div class="dj__kv">
              <span class="dj__k">Data</span>
              <span class="dj__v dj__num">{{ formattedDate }}</span>
            </div>
            <div class="dj__kv">
              <span class="dj__k">Corse</span>
              <span class="dj__v dj__num">{{ routes.length }}</span>
            </div>
            <div class="dj__kv">
              <span class="dj__k">Km totali</span>
              <span class="dj__v dj__num">{{ totalKm }} km</span>
            </div>
            <div class="dj__kv">
              <span class="dj__k">Durata totale</span>
              <span class="dj__v dj__num">{{ totalMin }} min</span>
            </div>
            <div
              v-if="journey?.modified_by_employee"
              class="dj__kv"
            >
              <span class="dj__k">Modificato da</span>
              <span class="dj__v">{{ nomePersona(journey?.modified_by_employee) }}</span>
            </div>
            <!-- Il costo con le TUE tariffe, quello che paga il tuo
                 cliente. Se manca la tariffa di una corsa non si mostra un
                 totale parziale: si dice che manca. -->
            <div class="dj__kv">
              <span class="dj__k">Imponibile</span>
              <span class="dj__v dj__num">{{ importo(prezzoPartner?.imponibile) }}</span>
            </div>
            <!-- La benzina fatta dal driver alle tue auto: rimborso spese a
                 parte, senza IVA sopra. La scrive Simba, corsa per corsa. -->
            <div
              v-if="benzina"
              class="dj__kv"
            >
              <span class="dj__k">Benzina</span>
              <span class="dj__v dj__num">{{ importo(benzina) }}</span>
            </div>
            <div class="dj__kv dj__kv--last dj__kv--total">
              <span class="dj__k dj__kTotal">{{ benzina ? 'Totale IVA e benzina incluse' : 'Totale IVA inclusa' }}</span>
              <span class="dj__total">{{ importo(totaleConBenzina) }}</span>
            </div>
            <p
              v-if="notaPrezzo"
              class="dj__meta dj__note"
            >
              {{ notaPrezzo }}
            </p>
          </div>
        </section>
      </aside>
    </div>

    <!-- Set fotografici della corsa (pre-signed URL a scadenza) -->
    <el-dialog
      v-model="photosVisible"
      title="Foto della corsa"
      width="80%"
      :destroy-on-close="true"
    >
      <p
        v-if="photosLoading"
        class="dj__dialogMsg"
      >
        Caricamento foto…
      </p>
      <p
        v-else-if="photoSets.length === 0"
        class="dj__dialogMsg"
      >
        Nessuna foto per questa corsa: le carica il driver alla partenza e
        all'arrivo.
      </p>
      <div v-else>
        <div
          v-for="set in photoSets"
          :key="set.id"
          class="dj__photoSet"
        >
          <h4 class="dj__photoTitle">
            {{ set.phase === 'PARTENZA' ? 'Partenza' : 'Arrivo' }}
            <span class="dj__meta">caricate il {{ formatPhotoDate(set.created_at) }}</span>
          </h4>
          <div class="dj__photos">
            <el-image
              v-for="(url, key) in set.urls"
              :key="key"
              :src="url"
              :preview-src-list="Object.values(set.urls)"
              fit="cover"
              class="dj__photo"
            />
          </div>
        </div>
        <p class="dj__meta">
          I link delle immagini scadono dopo pochi minuti: riapri la finestra per rigenerarli.
        </p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import journeyAPI from '../services/journeyAPI';
import partnerAPI from '@/services/partnerAPI';
import { corsaState, routeState } from '@/services/agendaRows';
import { isLoggedStore } from '../store/logged';
import { ElMessage, ElMessageBox } from 'element-plus';
import dayjs from 'dayjs';
import JourneyLegsEditor from '../Components/JourneyLegsEditor.vue';
import MappaSpostamenti from '../Components/MappaSpostamenti.vue';
import { journeysInWindow } from '@/services/agendaAPI';
import { analizzaCatene, chiaveLuogo, corseDaSpostamento, rientriInParole, vistaSedi } from '@/utils/catene';
import bookingAPI from '@/services/bookingAPI';
import StatusPill from '@/Components/ui/StatusPill.vue';
import ServiceChip from '@/Components/ui/ServiceChip.vue';

// L'ordine delle corse: per orario; a parità (più auto insieme) prima quella
// con la targa, poi per targa. Le corse non hanno una posizione salvata, e
// senza questo l'auto «principale» cambiava a ogni riapertura.
const ordineCorse = (a, b) =>
  (new Date(a.dateStart) - new Date(b.dateStart))
  || (Number(!a.targa) - Number(!b.targa))
  || String(a.targa || '').localeCompare(String(b.targa || ''))
  || String(a.id || '').localeCompare(String(b.id || ''));

export default {
  name: 'DetailsJourneyPage',
  components: { JourneyLegsEditor, StatusPill, ServiceChip, MappaSpostamenti },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const storeLogin = isLoggedStore();
    const id = route.params.id;

    const journey = ref(null);
    const routes = ref([]);
    const loading = ref(true);

    // Prezzo con le tariffe del partner: calcolato dal backend, non
    // salvato. Best-effort: se la chiamata fallisce la pagina resta usabile.
    const prezzoPartner = ref(null);
    const euro = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    // Un importo che non si può calcolare è «—», non «€ 0,00».
    const importo = (n) => (n === null || n === undefined ? '—' : euro(n));
    const getPrezzoPartner = async () => {
      try {
        const res = await journeyAPI.getPartnerPrice(id);
        prezzoPartner.value = res.data;
      } catch (error) {
        console.warn('Prezzo partner non disponibile:', error?.message);
        prezzoPartner.value = null;
      }
    };
    // Il testo della «i» accanto al driver: come rientra, e chi porta a bordo.
    const infoRientro = (leg) => {
      const r = rientri.value?.get?.(leg.id) ?? null;
      if (!r) return '';
      const frase = (t) => String(t || '').trim().replace(/\.$/, '');
      return [r.arriva, r.testo, r.porta, r.aBordo].map(frase).filter(Boolean).join('. ') + '.';
    };

    const notaPrezzo = computed(() => {
      if (!prezzoPartner.value) return 'Costo non disponibile al momento.';
      if (!prezzoPartner.value.complete) {
        return 'Totale non calcolabile: a una corsa mancano la tariffa o i km.';
      }
      // Il costo c'è: niente da spiegare.
      return '';
    });

    const getJourney = async () => {
      try {
        const response = await journeyAPI.getJourneysById(id);
        journey.value = response.data?.[0] || null;
        // Solo le corse attive inserite in creazione: escludo i rientri jolly
        // (movement:false) e le corse ELIMINATA in attesa di approvazione admin.
        routes.value = (journey.value?.routes || [])
          .filter((r) => r.movement !== false && r.status !== 'ELIMINATA')
          .sort(ordineCorse);
        caricaGiornata();
      } catch (error) {
        console.error('Errore nel recupero del journey:', error.message);
      }
    };

    // ---- F3: foto delle corse (fasi PARTENZA/ARRIVO, pre-signed URL) --------
    const photosVisible = ref(false);
    const photosLoading = ref(false);
    const photoSets = ref([]);

    const openRoutePhotos = async (leg) => {
      photosVisible.value = true;
      photosLoading.value = true;
      photoSets.value = [];
      try {
        const response = await journeyAPI.getRoutePhotos(leg.id);
        photoSets.value = (response.data || []).sort((a) =>
          a.phase === 'PARTENZA' ? -1 : 1
        );
      } catch (error) {
        // 403/404: toast dall'interceptor; il dialog resta col messaggio vuoto
        console.error('getRoutePhotos:', error?.response?.status);
      } finally {
        photosLoading.value = false;
      }
    };

    const formatPhotoDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '');

    // ---- Costo di ogni corsa --------------------------------------------
    // Le righe di `partner-price`, per id della corsa.
    const costi = computed(() => {
      const out = {};
      (prezzoPartner.value?.routes || []).forEach((r) => {
        if (r.price !== null && r.price !== undefined) {
          out[r.id] = { ok: true, testo: `${euro(r.price)} + IVA` };
        } else {
          out[r.id] = {
            ok: false,
            testo: r.km === null ? 'non calcolabile: mancano i km' : 'tariffa non impostata',
          };
        }
      });
      return out;
    });

    // ---- Allegati delle corse ------------------------------------------------
    // Per id della corsa: { stato: 'loading' | 'ok' | 'errore', lista }.
    const allegati = ref({});
    // Per id della corsa: true mentre un file è in caricamento.
    const inCaricamento = ref({});
    // Il backend accetta richieste grandi, ma un file enorme in base64 rende
    // la pagina lenta per chiunque lo carichi: si ferma prima (come i booking).
    const MAX_MB = 20;

    const leggiAllegati = async (rid) => {
      try {
        const res = await journeyAPI.getRouteAttachments(rid);
        allegati.value[rid] = {
          stato: 'ok',
          lista: Array.isArray(res.data) ? res.data : [],
        };
      } catch (error) {
        console.error('getRouteAttachments:', error?.response?.status);
        allegati.value[rid] = { stato: 'errore', lista: [] };
      }
    };

    const caricaAllegati = async () => {
      const ids = routes.value.map((r) => r.id).filter(Boolean);
      allegati.value = Object.fromEntries(
        ids.map((rid) => [rid, { stato: 'loading', lista: [] }])
      );
      await Promise.all(ids.map(leggiAllegati));
    };

    const leggiFile = (file) => new Promise((resolve, reject) => {
      const lettore = new FileReader();
      lettore.onload = () => resolve(lettore.result);
      lettore.onerror = () => reject(lettore.error);
      lettore.readAsDataURL(file);
    });

    const caricaAllegato = async (leg, evento) => {
      const file = evento.target.files && evento.target.files[0];
      evento.target.value = '';
      if (!file) return;
      if (file.size > MAX_MB * 1024 * 1024) {
        ElMessage({ type: 'warning', message: `Il file supera i ${MAX_MB} MB.` });
        return;
      }
      inCaricamento.value[leg.id] = true;
      try {
        const dataUri = await leggiFile(file);
        await journeyAPI.uploadRouteAttachment(leg.id, { file: dataUri, fileName: file.name });
        ElMessage({ type: 'success', message: 'Allegato caricato.' });
        await leggiAllegati(leg.id);
      } catch (error) {
        console.error('Allegato non caricato:', error?.response?.status, error?.message);
        ElMessage({ type: 'error', message: 'Allegato non caricato. Riprova.' });
      } finally {
        inCaricamento.value[leg.id] = false;
      }
    };

    const eliminaAllegato = async (leg, allegato) => {
      try {
        await ElMessageBox.confirm(`Eliminare «${allegato.file_name}»?`, 'Elimina allegato', {
          confirmButtonText: 'Elimina',
          cancelButtonText: 'Annulla',
          type: 'warning',
        });
      } catch (e) {
        return; // annullato
      }
      try {
        await journeyAPI.deleteRouteAttachment(leg.id, allegato.id);
        ElMessage({ type: 'success', message: 'Allegato eliminato.' });
        await leggiAllegati(leg.id);
      } catch (error) {
        console.error('Allegato non eliminato:', error?.response?.status);
        ElMessage({ type: 'error', message: 'Allegato non eliminato. Riprova.' });
      }
    };

    // I link sono pre-signed e scadono dopo pochi minuti, mentre la pagina può
    // restare aperta a lungo: all'apertura si rilegge l'elenco e si usa il link
    // appena generato. La finestra si apre SUBITO, dentro il click, altrimenti
    // il browser la blocca come popup.
    const apriAllegato = async (leg, allegato) => {
      const finestra = window.open('', '_blank');
      try {
        const res = await journeyAPI.getRouteAttachments(leg.id);
        const lista = Array.isArray(res.data) ? res.data : [];
        allegati.value[leg.id] = { stato: 'ok', lista };
        const fresco = lista.find((x) => x.id === allegato.id);
        if (!fresco?.url) throw new Error('allegato non più presente');
        if (!finestra) throw new Error('finestra bloccata');
        finestra.opener = null;
        finestra.location.href = fresco.url;
      } catch (error) {
        if (finestra) finestra.close();
        console.error('apriAllegato:', error?.message);
        ElMessage({ type: 'error', message: 'Allegato non disponibile. Riprova.' });
      }
    };

    const dimensione = (b) => {
      if (!b) return '—';
      if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
      return `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
    };

    // ---- Formatters ----------------------------------------------------------
    const formatDateTime = (dt) => (dt ? dayjs(dt).format('HH:mm') : '—');

    const formatAddress = (point) => {
      if (!point) return 'Non disponibile';
      return `${point.address || ''} ${point.street_number || ''}, ${point.city || ''}`
        .replace(/\s+/g, ' ')
        .replace(/\s,/, ',')
        .trim();
    };

    // all'italiana: «12,5», non «12.50»
    const numero = (v) => {
      const n = Number(v);
      return (Number.isFinite(n) ? n : 0).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const iniziali = (s) => s.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

    // ---- Metriche (snapshot dal backend) -------------------------------------
    const giornoJourney = computed(() => journey.value?.date || route.params.date);

    const formattedDate = computed(() =>
      giornoJourney.value ? dayjs(giornoJourney.value).format('DD/MM/YYYY') : '—'
    );
    const giornoEsteso = computed(() => {
      if (!giornoJourney.value) return '—';
      return new Date(giornoJourney.value).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
    });

    const totalKm = computed(() => numero(journey.value?.total_km));
    // Nome e cognome di un dipendente; vuoto se non c'è (l'ha creato Simba).
    const nomePersona = (p) => (p ? `${p.name || ''} ${p.surname || ''}`.trim() : '');
    const totalMin = computed(() => Math.round(Number(journey.value?.total_min) || 0));

    const corseLabel = computed(() =>
      routes.value.length === 1 ? '1 corsa' : `${routes.value.length} corse`
    );

    const status = computed(() => journey.value?.status || '');

    // Toni e parole dell'Agenda (`routeState`): lo stesso stato ha lo stesso
    // colore nella riga dell'Agenda e nel dettaglio che quella riga apre.
    // Al maschile per lo spostamento, al femminile per la corsa.
    const MASCHILE = {
      Conclusa: 'Concluso',
      Confermata: 'Confermato',
      Modificata: 'Modificato',
      Eliminata: 'Eliminato',
      Programmata: 'Programmato',
    };
    const statoPill = computed(() => {
      const s = routeState(status.value);
      return { kind: s.kind, label: MASCHILE[s.label] || s.label };
    });
    // Con l'avanzamento dell'app del driver, se è partito.
    const statoCorsa = (leg) => corsaState(leg);

    // ---- Regola del cut-off ore 15:00 (lock modifiche) -----------------------
    // Si può modificare solo FINO alle 15:00 del giorno PRECEDENTE alla partenza.
    // Valutata in tempo reale sia all'ingresso in edit mode sia al SALVATAGGIO.
    const isBeforeCutoff = () => {
      const start = giornoJourney.value;
      if (!start) return false;
      const deadline = dayjs(start)
        .subtract(1, 'day')
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);
      return dayjs().isBefore(deadline);
    };
    const canEdit = computed(() => isBeforeCutoff());

    // ---- Inline Edit Mode ----------------------------------------------------
    const isEditing = ref(false);
    const editRoutes = ref([]);
    const legsEditor = ref(null);
    const editSession = ref(0);

    // Stesse parole dell'editor: la stessa corsa non può chiamarsi in due modi
    // a seconda della pagina. Singolo = 2 operatori (driver e jolly), a catena
    // = 1. Le tratte storiche senza flag valgono singolo.
    const operatoriLabel = (leg) =>
      leg.two_operators === false ? 'A catena' : 'Singolo';

    // Driver e jolly. Il jolly c'è solo sulle corse singole (2 operatori);
    // su quelle a catena il secondo driver, se c'è, è il passeggero.
    const persone = (leg) => {
      const out = [];
      const p = (x, ruolo) => ({
        ruolo,
        nome: x ? [x.name, x.surname].filter(Boolean).join(' ') : '',
        telefono: x?.telephone_number || '',
      });
      out.push(p(leg.driver, 'Driver'));
      if (leg.two_operators !== false && leg.rientro_altrove) {
        // Singolo senza jolly: Simba ha deciso che il driver rientra con i
        // mezzi pubblici (lo dice la riga «Rientro del driver»).
      } else if (leg.two_operators !== false) {
        out.push(p(leg.jolly_driver, 'Jolly'));
      } else {
        // Sulla corsa a catena gli altri sono PASSEGGERI: driver di altre
        // corse che rientrano a bordo di questa auto (fino a 4). Per ogni
        // corsa che rientra con questa c'è un posto, anche se Simba non ha
        // ancora assegnato il driver.
        const passeggeri = leg.passengers || [];
        routes.value.forEach((r, k) => {
          if (r.rientro_route_id !== leg.id) return;
          const pp = passeggeri.find((x) => x.from_route_id === r.id);
          out.push({ ...p(pp?.driver, `Passeggero · driver della corsa ${k + 1}`), chiave: `da-${r.id}` });
        });
        passeggeri
          .filter((x) => !routes.value.some((r) => r.id === x.from_route_id && r.rientro_route_id === leg.id))
          .forEach((x) => out.push({ ...p(x.driver, 'Passeggero · rientra a bordo'), chiave: x.id }));
        if (leg.jolly_driver) out.push(p(leg.jolly_driver, 'Passeggero · rientra a bordo'));
      }
      return out;
    };


    // Gli altri spostamenti dello stesso giorno: per il disegno (chi rientra
    // a bordo di una loro corsa) e per l'editor. Best-effort.
    const giornata = ref([]);
    const caricaGiornata = async () => {
      const giorno = journey.value?.date ? dayjs(journey.value.date).format('YYYY-MM-DD') : '';
      if (!giorno) return;
      try {
        giornata.value = (await journeysInWindow(giorno, giorno)).rows || [];
      } catch (e) {
        giornata.value = [];
      }
    };
    // Le sedi del partner, per chiamare i luoghi col loro nome nel disegno
    // (il punto salvato ha solo l'indirizzo).
    const sedi = ref([]);
    bookingAPI
      .getHeadquartersOfPartner()
      .then((res) => { sedi.value = res.data?.data ?? res.data ?? []; })
      .catch(() => { sedi.value = []; });
    const nomeLuogo = (p) => {
      if (!p) return '';
      const sede = sedi.value.find((h) => h.name && chiaveLuogo(h) === chiaveLuogo(p));
      return sede ? sede.name : [p.address, p.street_number].filter(Boolean).join(' ') || p.city || '';
    };
    // Il nome della sede di un capo della corsa: dall'id salvato sulla corsa,
    // o dall'indirizzo se la corsa non lo ha. Vuoto = indirizzo libero.
    const nomeSede = (p, sedeId) => {
      const sede = (sedeId && sedi.value.find((h) => h.id === sedeId))
        || (p && sedi.value.find((h) => h.name && chiaveLuogo(h) === chiaveLuogo(p)));
      return sede?.name || '';
    };
    const tappe = (leg) => [
      {
        chiave: 'da',
        etichetta: 'Partenza',
        ora: formatDateTime(leg.dateStart),
        sede: nomeSede(leg.startPoint, leg.start_headquarter_id),
        indirizzo: formatAddress(leg.startPoint),
      },
      {
        chiave: 'a',
        etichetta: 'Arrivo',
        ora: formatDateTime(leg.dateEnd),
        sede: nomeSede(leg.endPoint, leg.end_headquarter_id),
        indirizzo: formatAddress(leg.endPoint),
      },
    ];

    const corseSalvate = computed(() =>
      corseDaSpostamento({ ...(journey.value || {}), routes: routes.value }, giornata.value, nomeLuogo)
    );
    const mappaSalvata = computed(() => vistaSedi(corseSalvate.value));
    const rientri = computed(() => rientriInParole(corseSalvate.value));

    // Corsa «incompleta»: mancano i dati che
    // inserisce il partner, cioè veicolo e percorso. Il driver non conta.
    const vuoto = (v) => v === null || v === undefined || String(v).trim() === '';
    const datiMancanti = (leg) => {
      const out = [];
      // Con la targa prova la targa non c'è ancora: non è un dato mancante.
      if (vuoto(leg.targa) && leg.immatricolato !== false) out.push('targa');
      if (vuoto(leg.marca)) out.push('marca');
      if (vuoto(leg.modello)) out.push('modello');
      if (!leg.startPoint || vuoto(leg.startPoint.address)) out.push('partenza');
      if (!leg.endPoint || vuoto(leg.endPoint.address)) out.push('arrivo');
      if (vuoto(leg.dateStart)) out.push('orario');
      return out;
    };

    // Corse eliminate in edit mode (soft-delete): committate SOLO al salvataggio.
    const pendingDeletions = ref([]);

    // Seed dell'editor dalle tappe persistite: TUTTE attive. Il punto salvato
    // è un `Point` (il suo id non è quello della sede): la sede la dice la
    // corsa (`start_headquarter_id` / `end_headquarter_id`). Va riportata,
    // altrimenti al salvataggio la corsa perde il collegamento alla sede e chi
    // vede solo le proprie sedi non la vede più.
    const toEditorRoutes = () =>
      (journey.value?.routes || [])
        .filter((r) => r.movement !== false && r.status !== 'ELIMINATA')
        .sort(ordineCorse)
        .map((r) => ({
          // sourceId: id della Route persistita: si aggiorna sul posto, o va in
          // soft-delete se la si toglie.
          sourceId: r.id,
          startPoint: r.startPoint ? { ...r.startPoint, id: r.start_headquarter_id || undefined } : null,
          endPoint: r.endPoint ? { ...r.endPoint, id: r.end_headquarter_id || undefined } : null,
          startText: r.startPoint ? formatAddress(r.startPoint) : '',
          endText: r.endPoint ? formatAddress(r.endPoint) : '',
          dateStart: r.dateStart ? dayjs(r.dateStart).format('HH:mm') : '',
          dateEnd: r.dateEnd ? dayjs(r.dateEnd).format('HH:mm') : '',
          details: r.details || '',
          marca: r.marca || '',
          modello: r.modello || '',
          targa: r.targa || '',
          gearbox_type: r.gearbox_type || '',
          // Al negativo come nell'editor: la casella si spunta per l'eccezione.
          nonImmatricolato: r.immatricolato === false,
          isShuttle: r.two_operators !== false,
          autoChained: false,
          rientroSourceId: r.rientro_route_id || null,
          andataSourceId: r.andata_route_id || null,
        }));

    // L'editor segnala l'eliminazione di una corsa PERSISTITA: la accodo per
    // il soft-delete (ELIMINATA) da eseguire al salvataggio.
    const onRouteRemoved = (sourceId) => {
      if (sourceId) pendingDeletions.value.push(sourceId);
    };

    const editorDate = computed(() => dayjs(giornoJourney.value).format('YYYY-MM-DD'));

    // Una pagina aperta prima delle 15:00 resta aperta anche dopo: la scadenza
    // si ricontrolla al momento di agire, non solo quando si carica.
    const scadenzaPassata = async () => {
      if (isBeforeCutoff()) return false;
      ElMessage({
        type: 'warning',
        message: 'Sono passate le 15:00 del giorno prima: modifiche ed eliminazioni sono chiuse. Per cambiare qualcosa contatta Simba.',
      });
      isEditing.value = false;
      await ricarica();
      return true;
    };

    const enterEdit = async () => {
      if (await scadenzaPassata()) return;
      editRoutes.value = toEditorRoutes();
      pendingDeletions.value = [];
      editSession.value += 1; // forza il re-mount/re-seed dell'editor
      isEditing.value = true;
    };
    const cancelEdit = () => {
      pendingDeletions.value = []; // annullate: nessun soft-delete eseguito
      isEditing.value = false;
    };

    // Dopo una modifica cambiano km, operatori e corse: vanno riletti anche
    // costi e allegati, non solo le corse.
    const ricarica = async () => {
      loading.value = true;
      try {
        await getJourney();
        await Promise.all([getPrezzoPartner(), caricaAllegati()]);
      } finally {
        loading.value = false;
      }
    };

    const saveEdit = async () => {
      const editor = legsEditor.value;
      if (!editor) return;
      // Dopo le 15:00 del giorno prima non si salva niente, nemmeno le
      // eliminazioni.
      if (await scadenzaPassata()) return;

      // ⚠️ PRIMA il controllo del modulo, POI le eliminazioni: prima era il
      // contrario, e una corsa o un'auto tolta finiva ELIMINATA anche se il
      // salvataggio poi si fermava («dati auto mancanti»), e restava tolta
      // pure annullando la modifica.
      const check = editor.validate();
      if (!check.ok) {
        ElMessage({ message: check.message, type: 'error' });
        return;
      }

      // Le eliminazioni passano dall'endpoint di stato: la corsa transita in
      // ELIMINATA (con decisione di contabilizzazione pendente lato admin).
      // Vanno fatte prima della modifica: le corse rimaste si aggiornano sul
      // posto (ognuna col suo id), e restano driver, foto e minuti.
      try {
        for (const routeId of pendingDeletions.value) {
          await journeyAPI.setRouteStatus(routeId, 'ELIMINATA');
        }
        pendingDeletions.value = [];
      } catch (error) {
        // foto-gate/409/403: il toast col messaggio del server arriva
        // dall'interceptor di response
        console.error('setRouteStatus:', error?.response?.status);
        return;
      }
      try {
        let partnerId = journey.value?.partner?.id;
        if (!partnerId) {
          const respP = await partnerAPI.getPartnerByEmployee(
            storeLogin.employee.id
          );
          partnerId = respP.data?.[0]?.headquarters?.[0]?.partner?.id;
        }
        const payload = {
          date: editorDate.value,
          partner_id: partnerId,
          employee_id: storeLogin.employee?.id,
          routes: editor.buildRoutesDto(),
        };
        const resp = await journeyAPI.modifyJourney(id, payload);
        if (resp.status === 200 || resp.status === 201) {
          ElMessage({ message: 'Modifiche salvate', type: 'success' });
          isEditing.value = false;
          await ricarica();
        }
      } catch (error) {
        // 400 edit-lock / limite, 403 journey altrui: toast dall'interceptor
        console.error('modifyJourney:', error?.response?.status);
      }
    };

    const goBack = () => router.back();

    // ---- Elimina corsa -------------------------------------------------------
    // La singola corsa: lo stesso PATCH di stato del cestino (ELIMINATA), che
    // avvisa i driver assegnati. Se era l'ultima, lo spostamento della
    // giornata non ha più niente da mostrare e si torna all'agenda.
    // L'andata e il suo ritorno vanno insieme, come nell'editor: la corsa
    // che prosegue la catena di un'altra (il driver arriva con quella) se ne
    // va con lei, e da sola non si elimina (il driver dell'andata
    // resterebbe a piedi).
    const catene = computed(() => analizzaCatene(routes.value.map((r) => ({
      id: r.id,
      da: chiaveLuogo(r.startPoint),
      a: chiaveLuogo(r.endPoint),
      inizio: new Date(r.dateStart).getTime(),
      fine: new Date(r.dateEnd).getTime(),
      catena: r.two_operators === false,
      rientro: r.two_operators === false ? r.rientro_route_id || null : null,
    }))));
    const numeroCorsa = (routeId) => routes.value.findIndex((r) => r.id === routeId) + 1;

    const eliminandoCorsa = ref('');
    const eliminaCorsa = async (leg, i) => {
      if (!leg?.id || eliminandoCorsa.value) return;
      if (await scadenzaPassata()) return;
      const andata = catene.value.padre.get(leg.id);
      if (andata) {
        ElMessage({
          type: 'warning',
          message: `La corsa ${i + 1} è il ritorno della corsa ${numeroCorsa(andata)}: si elimina eliminando quella.`,
        });
        return;
      }
      const ritorno = catene.value.figlio.get(leg.id) || null;
      try {
        await ElMessageBox.confirm(
          (ritorno ? `Con l'andata si elimina anche il ritorno (corsa ${numeroCorsa(ritorno)}). ` : '') +
            'I driver già assegnati riceveranno l\'avviso. ' +
            'Le altre corse della giornata restano come sono.',
          ritorno ? `Eliminare le corse ${i + 1} e ${numeroCorsa(ritorno)}?` : `Eliminare la corsa ${i + 1}?`,
          { confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning' }
        );
      } catch {
        return;
      }
      eliminandoCorsa.value = leg.id;
      try {
        await journeyAPI.setRouteStatus(leg.id, 'ELIMINATA');
        if (ritorno) await journeyAPI.setRouteStatus(ritorno, 'ELIMINATA');
        ElMessage({
          type: 'success',
          message: ritorno ? `Corse ${i + 1} e ${numeroCorsa(ritorno)} eliminate.` : `Corsa ${i + 1} eliminata.`,
        });
        const restano = routes.value.filter((r) => r.id !== leg.id && r.id !== ritorno).length;
        if (!restano) {
          const giorno = journey.value?.date ? dayjs(journey.value.date).format('YYYY-MM-DD') : null;
          router.push(giorno ? { path: '/agenda', query: { giorno } } : { path: '/agenda' });
          return;
        }
        await ricarica();
      } catch (error) {
        // Il messaggio del server (es. scadenza passata) lo mostra l'interceptor.
        console.error('Eliminazione corsa:', error?.response?.status);
        await ricarica();
      } finally {
        eliminandoCorsa.value = '';
      }
    };

    onMounted(ricarica);

    // Benzina delle corse (rimborso spese a parte): si somma al totale.
    const benzina = computed(() =>
      Math.round(routes.value.reduce((s, r) => s + Number(r.carburante || 0), 0) * 100) / 100);
    const totaleConBenzina = computed(() => {
      const t = prezzoPartner.value?.tot_fattura;
      if (t === null || t === undefined) return t;
      return Math.round((Number(t) + benzina.value) * 100) / 100;
    });

    return {
      benzina, totaleConBenzina,
      id,
      journey,
      routes,
      loading,
      prezzoPartner,
      importo,
      notaPrezzo, infoRientro,
      operatoriLabel,
      persone,
      giornata,
      mappaSalvata, tappe,
      rientri,
      iniziali,
      numero,
      datiMancanti,
      formatDateTime,
      formatAddress,
      formattedDate,
      giornoEsteso,
      corseLabel,
      totalKm,
      nomePersona,
      totalMin,
      status,
      statoPill,
      statoCorsa,
      canEdit,
      isEditing,
      editRoutes,
      editSession,
      editorDate,
      legsEditor,
      enterEdit,
      cancelEdit,
      saveEdit,
      onRouteRemoved,
      goBack,
      photosVisible,
      photosLoading,
      photoSets,
      openRoutePhotos,
      formatPhotoDate,
      costi,
      allegati,
      inCaricamento,
      caricaAllegato,
      eliminaAllegato,
      eliminandoCorsa,
      eliminaCorsa,
      apriAllegato,
      dimensione,
    };
  },
};
</script>

<!--
  Stessi valori del dettaglio prenotazione (`.det__*`): card a 6px, bordo
  --color-border, testata 13/18px, etichette in maiuscoletto grigio.
-->
<style scoped>
.dj { padding: 4px 8px 32px; text-align: left; }

/* ---------- intestazione ---------- */
.dj__head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 12px; flex-wrap: wrap; }
.dj__back { width: 30px; height: 30px; border: 1px solid var(--color-border); background: #fff; border-radius: 6px; font-size: 15px; color: var(--color-text-secondary); cursor: pointer; flex: none; }
.dj__back:hover { border-color: var(--color-primary); color: var(--color-primary); }
.dj__headText { flex: 1 1 320px; min-width: 0; }
.dj__titleRow { display: flex; align-items: center; gap: 11px; flex-wrap: wrap; }
.dj__title { margin: 0; font-size: 23px; font-weight: 700; letter-spacing: -0.015em; }
.dj__title::first-letter { text-transform: uppercase; }
.dj__sub { display: flex; align-items: center; gap: 9px; margin-top: 5px; font-size: 12.5px; color: var(--color-text-secondary); }
.dj__actions { display: flex; align-items: center; gap: 8px; }
.dj__actions--foot {
  justify-content: flex-end;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.dj__now { display: flex; align-items: center; gap: 9px; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 11px 16px; font-size: 13px; font-weight: 600; margin-bottom: 16px; }
.dj__dot { width: 8px; height: 8px; border-radius: 50%; background: var(--state-attn); flex: none; }

/* ---------- impaginazione ---------- */
.dj__grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
.dj__col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.dj__aside { position: sticky; top: 12px; display: flex; flex-direction: column; gap: 16px; }

/* ---------- card ---------- */
.dj__card { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
/* Testata su fondo grigio: separa le card fra loro e dal corpo, che su una
   pagina fatta solo di card bianche si confondevano in un unico bianco. */
.dj__cardHead { display: flex; align-items: center; gap: 11px; flex-wrap: wrap; padding: 11px 18px; background: var(--color-bg-tertiary); border-bottom: 1px solid var(--color-border); }
.dj__cardHead h3 { margin: 0; font-size: 14.5px; font-weight: 600; }
.dj__cardBody { padding: 16px 18px; }
.dj__spacer { flex: 1 1 auto; }
.dj__tag { font-size: 11px; font-weight: 650; color: var(--state-muted); background: var(--state-muted-bg); border-radius: 4px; padding: 1px 8px; }
/* Condizione del veicolo che cambia la tariffa: colore d'attenzione. */
.dj__tag--imm { color: #92400e; background: #fef3c7; }
.dj__tool { display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 12.5px; font-weight: 600; color: var(--color-text-secondary); background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 4px 10px; cursor: pointer; }
.dj__tool:hover { border-color: var(--color-primary); color: var(--color-primary); }
.dj__tool:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.dj__tool--danger { color: var(--state-danger, #b42318); }
.dj__tool--danger:hover { border-color: var(--state-danger, #b42318); color: var(--state-danger, #b42318); }
.dj__tool:disabled { opacity: 0.6; cursor: default; }

.dj__missing { margin: 0 0 12px; font-size: 12.5px; font-weight: 600; color: var(--state-attn); }

/* ---------- percorso ---------- */
.dj__legTop { display: grid; grid-template-columns: minmax(0, 1fr) 74px; gap: 22px; align-items: start; }
/* Partenza e arrivo: etichetta, orario e luogo sulla stessa riga. */
.dj__stops { display: flex; flex-direction: column; }
.dj__stop {
  display: grid; grid-template-columns: 72px 52px minmax(0, 1fr); gap: 4px 14px;
  align-items: baseline; padding: 8px 0;
}
.dj__stop + .dj__stop { border-top: 1px dashed var(--color-border); }
.dj__stop:first-child { padding-top: 0; }
.dj__stopK {
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--color-text-secondary);
}
.dj__stopH { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; }
.dj__stopN { font-size: 13.5px; font-weight: 650; overflow-wrap: anywhere; }
.dj__addr { font-size: 13px; line-height: 1.45; overflow-wrap: anywhere; }
/* Il nome della sede e, alla sua destra, l'indirizzo (va a capo se non ci sta). */
.dj__stopP { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 10px; min-width: 0; }
.dj__addr--sub { font-size: 13.5px; color: #404040; }
.dj__km { text-align: right; }
.dj__kmN { font-size: 19px; font-weight: 700; font-variant-numeric: tabular-nums; }
.dj__kmN--min { margin-top: 8px; }
.dj__kmL { font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-muted); }

.dj__kvRow { display: flex; gap: 28px; flex-wrap: wrap; margin-top: 14px; }
.dj__kvCell { display: flex; align-items: baseline; gap: 8px; }

.dj__sep { height: 1px; background: var(--color-bg-tertiary); margin: 15px 0 13px; }

/* ---------- veicolo ----------
   Un riquadro grigio in cima alla card, tutto a sinistra: targa disegnata
   come una targa (bordo scuro, banda navy del brand), marca e modello in
   grande accanto. Quando manca un dato si dice «mancante» nel tono «da
   sistemare», invece di un trattino che passa inosservato. */
.dj__car { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 16px; margin-bottom: 16px; padding: 12px 14px; background: var(--color-bg-tertiary); border: 1px solid var(--color-border); border-radius: 6px; }
.dj__carPlate { position: relative; display: inline-flex; align-items: center; flex: none; min-height: 34px; padding: 0 12px 0 18px; background: #fff; border: 1.5px solid var(--color-text-primary); border-radius: 4px; overflow: hidden; font-size: 17px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; font-variant-numeric: tabular-nums; }
.dj__carPlate::before { content: ''; position: absolute; top: 0; bottom: 0; left: 0; width: 6px; background: #232c51; /* navy del brand: --color-brand qui è il viola dello spostamento interno */ }
.dj__carPlate--empty { padding-left: 12px; border-style: dashed; border-color: var(--state-attn-line); color: var(--state-attn); font-size: 13px; letter-spacing: 0; text-transform: none; }
.dj__carPlate--empty::before { display: none; }
.dj__carInfo { min-width: 0; }
.dj__carMeta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 2px; }
.dj__carName { font-size: 16px; font-weight: 700; letter-spacing: -0.01em; overflow-wrap: anywhere; }
.dj__carName--empty { font-size: 13px; font-weight: 600; color: var(--state-attn); }
.dj__carNote { flex-basis: 100%; margin: 0; padding-top: 10px; border-top: 1px solid var(--color-border); font-size: 13px; line-height: 1.5; overflow-wrap: anywhere; }
.dj__carNoteT { white-space: pre-line; }
.dj__carNoteVuota { color: var(--color-text-secondary); font-style: italic; }
.dj__carNoteK { margin-right: 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary); }

/* ---------- persone ---------- */
.dj__giri { margin-top: 16px; }
.dj__people { display: flex; align-items: center; gap: 26px; flex-wrap: wrap; }
.dj__person { display: flex; align-items: center; gap: 9px; }
.dj__ava { width: 26px; height: 26px; border-radius: 50%; background: var(--state-muted-bg); color: var(--state-muted); display: flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 700; flex: none; }
.dj__ava--empty { background: #fff; border: 1px dashed var(--el-border-color-dark); color: var(--color-text-muted); }
.dj__personName { font-size: 13px; font-weight: 600; }
.dj__personName--empty { font-weight: 400; color: var(--color-text-muted); }
.dj__meta { font-size: 12px; color: var(--color-text-muted); }
.dj__tel { color: var(--color-primary); text-decoration: none; }
.dj__info {
  display: inline-grid; place-items: center; width: 16px; height: 16px; margin-left: 6px;
  border-radius: 50%; border: 1.5px solid var(--svc-fleet, #4b30f2); color: var(--svc-fleet, #4b30f2);
  font: italic 700 10.5px/1 Georgia, 'Times New Roman', serif; vertical-align: 1px; cursor: help;
}
.dj__info:focus-visible { outline: 2px solid var(--svc-fleet, #4b30f2); outline-offset: 2px; }
.dj__info--attn { border-color: var(--state-attn, #b45309); color: var(--state-attn, #b45309); }
.dj__tel:hover { text-decoration: underline; }

/* ---------- allegati ---------- */
.dj__atts { padding: 12px 18px 14px; border-top: 1px solid var(--color-border); background: var(--color-bg-secondary); }
.dj__atts .dj__meta { margin: 0; }
.dj__attsHead { display: flex; align-items: center; gap: 9px; margin-bottom: 6px; }
.dj__attsTitle { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary); }
.dj__count { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--color-text-secondary); background: var(--color-bg-tertiary); border-radius: 4px; padding: 0 7px; }
.dj__upload { position: relative; font-size: 12.5px; font-weight: 600; color: var(--color-primary); background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 4px 10px; cursor: pointer; }
.dj__upload:hover { border-color: var(--color-primary); }
.dj__upload:focus-within { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.dj__upload.is-busy { opacity: 0.6; cursor: default; }
.dj__file { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.dj__att { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-bottom: 1px solid var(--color-border); }
.dj__att:last-child { border-bottom: 0; }
.dj__attText { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; flex: 1 1 auto; }
.dj__attName { border: 0; background: none; padding: 0; font: inherit; font-size: 13px; font-weight: 600; color: var(--color-text-primary); text-align: left; overflow-wrap: anywhere; cursor: pointer; }
.dj__attName:hover { color: var(--color-primary); text-decoration: underline; }
.dj__attName:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
.dj__attDel { flex: none; width: 26px; height: 26px; border: 1px solid var(--color-border); border-radius: 6px; background: #fff; color: var(--state-danger); font-size: 15px; line-height: 1; cursor: pointer; }
.dj__attDel:hover { border-color: var(--state-danger); }

/* ---------- riepilogo ---------- */
.dj__kv { display: flex; align-items: baseline; justify-content: space-between; gap: 14px; padding: 7px 0; border-bottom: 1px solid var(--color-bg-tertiary); }
.dj__kv--last { border-bottom: 0; }
/* Il totale è il dato che si cerca nel riepilogo: riga in evidenza. */
.dj__kv--total { margin-top: 8px; padding: 10px 12px; background: var(--color-bg-tertiary); border: 1px solid var(--color-border); border-radius: 4px; }
.dj__k { font-size: 12.5px; color: var(--color-text-secondary); }
.dj__kTotal { font-size: 13px; font-weight: 600; color: var(--color-text-primary); }
.dj__v { font-size: 13px; font-weight: 600; text-align: right; }
.dj__v--attn { color: var(--state-attn); }
.dj__num { font-variant-numeric: tabular-nums; }
.dj__total { font-size: 17px; font-weight: 700; font-variant-numeric: tabular-nums; }
.dj__note { margin: 8px 0 0; line-height: 1.5; }

.dj__empty { background: #fff; border: 1px dashed var(--color-border); border-radius: 6px; padding: 32px; text-align: center; color: var(--color-text-secondary); }

/* ---------- foto ---------- */
.dj__dialogMsg { margin: 0; padding: 24px; text-align: center; color: var(--color-text-secondary); }
.dj__photoSet { margin-bottom: 20px; }
.dj__photoTitle { display: flex; align-items: baseline; gap: 10px; margin: 0 0 8px; font-size: 14px; font-weight: 600; }
.dj__photos { display: flex; gap: 10px; flex-wrap: wrap; }
.dj__photo { width: 150px; height: 150px; border-radius: 4px; border: 1px solid var(--color-border); }

@media (max-width: 1100px) {
  .dj__grid { grid-template-columns: minmax(0, 1fr); }
  .dj__aside { position: static; }
}
@media (max-width: 640px) {
  .dj__legTop { grid-template-columns: minmax(0, 1fr) auto; gap: 12px 16px; }
  .dj__stop { grid-template-columns: 62px 46px minmax(0, 1fr); gap: 4px 10px; }
  .dj__photo { width: calc(50% - 5px); height: auto; aspect-ratio: 1; }
}
</style>
